import { useMutation, useQueryClient } from "@tanstack/react-query"
import { FilePlus2 } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { CLASE } from "@/componentes/resumen/Listas"
import { esOperativa, type Partida, type Resumen } from "@/componentes/resumen/tipos"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia, type Tono } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

const clave = (p: Partida) => `${p.clase}:${p.ref_id}`
const centimos = (v: string | null) => (v === null ? 0 : Math.round(Number(v) * 100))
const dinero = (c: number) => `$ ${monto((c / 100).toFixed(2))}`

const TONO: Record<string, Tono> = {
  POR_PAGAR: "aviso",
  ABONADA: "aviso",
  ORDEN_BORRADOR: "neutro",
  ORDEN_EN_APROBACION: "info",
  POR_EJECUTAR: "exito",
}

/**
 * El planificador de pagos de los jueves (RM-7, 23/10; estructura §10). Cruza las CxP
 * efectivas con el disponible operativo —sin tocar la reserva y descontando los gastos de la
 * semana— y propone qué pagar el viernes: primero lo ya aprobado, después de lo más viejo a
 * lo más nuevo (`resumen_macro_calculo.proponer_pagos`, en el SERVIDOR).
 *
 * ⚠ **La autorización es la firma de cada orden** (decisión 9): de lo elegido que aún no
 * tiene orden se redacta en BORRADOR (`POST /cxp/{id}/orden-pago`), y la pantalla dice qué
 * falta por aprobar antes del viernes. El pago sale, como siempre, por Tesorería.
 */
/** Los pagos operativos y los gastos administrativos se eligen y se leen aparte (30/10). */
const GRUPOS = [
  { titulo: "Pagos operativos", de: esOperativa },
  { titulo: "Gastos administrativos", de: (p: Partida) => !esOperativa(p) },
]

export function Planificador({ r }: { r: Resumen }) {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeOrden = tienePermiso(usuario, "orden_pago:crear")
  const candidatas = r.cxp.partidas.filter((p) => p.suma)
  const [elegidas, setElegidas] = useState<Set<string>>(() => new Set(r.propuesta_pago))
  const [resultado, setResultado] = useState<string[]>([])

  const presupuesto = centimos(r.cobertura.operativo) - centimos(r.gastos_semana.total)
  const elegido = candidatas
    .filter((p) => elegidas.has(clave(p)))
    .reduce((t, p) => t + centimos(p.usd), 0)
  const queda = presupuesto - elegido
  const sinOrden = candidatas.filter(
    (p) =>
      elegidas.has(clave(p)) &&
      p.clase !== "ORDEN_PAGO" &&
      (p.estado === "POR_PAGAR" || p.estado === "ABONADA"),
  )
  const porAprobar = candidatas.filter(
    (p) =>
      elegidas.has(clave(p)) &&
      (p.estado === "ORDEN_BORRADOR" || p.estado === "ORDEN_EN_APROBACION"),
  )

  const generar = useMutation({
    mutationFn: async () => {
      const salida: string[] = []
      // De una en una: cada orden toma su correlativo.
      for (const p of sinOrden) {
        try {
          const o = await api.post<{ codigo: string }>(`/api/v1/cxp/${p.ref_id}/orden-pago`, {})
          salida.push(`${p.titulo}: ${o.codigo} en borrador`)
        } catch (fallo) {
          salida.push(
            `${p.titulo}: ${fallo instanceof ErrorApi ? fallo.message : "no se pudo redactar"}`,
          )
        }
      }
      return salida
    },
    onSuccess: (salida) => {
      setResultado(salida)
      clienteQuery.invalidateQueries({ queryKey: ["resumen-macro"] })
    },
  })

  const subtitulo = `Lo elegido para pagar el viernes: ${elegidas.size} cuenta(s), en dólares`
  const documento = (): DocumentoExportable => ({
    titulo: "Planificador del viernes",
    subtitulo,
    secciones: [
      {
        titulo: "Presupuesto del viernes",
        columnas: [
          { titulo: "Concepto", tipo: "texto" },
          { titulo: "USD", tipo: "monto" },
        ],
        filas: [
          { celdas: ["Para pagar sin tocar la reserva", presupuesto / 100] },
          { celdas: ["Elegido para el viernes", elegido / 100] },
          { celdas: [queda < 0 ? "Toca la reserva" : "Queda", Math.abs(queda) / 100] },
        ],
      },
      ...GRUPOS.map(({ titulo, de }) => ({
        titulo,
        columnas: [
          { titulo: "Elegida", tipo: "texto" as const },
          { titulo: "Quién y qué", tipo: "texto" as const },
          { titulo: "Detalle", tipo: "texto" as const },
          { titulo: "Expediente", tipo: "texto" as const },
          { titulo: "Paso", tipo: "texto" as const },
          { titulo: "Orden", tipo: "texto" as const },
          { titulo: "Vence", tipo: "fecha" as const },
          { titulo: "USD", tipo: "monto" as const },
        ],
        filas: candidatas.filter(de).map((p) => ({
          celdas: [
            elegidas.has(clave(p)) ? "Sí" : "No",
            p.titulo,
            [CLASE[p.clase], p.detalle].filter(Boolean).join(" · ") || null,
            p.expediente,
            r.estados_pago[p.estado ?? ""] ?? p.estado,
            p.orden,
            p.fecha?.slice(0, 10) ?? null,
            p.usd === null ? null : Number(p.usd),
          ],
        })),
      })),
    ],
  })

  const alternar = (k: string) =>
    setElegidas((antes) => {
      const nuevo = new Set(antes)
      if (nuevo.has(k)) nuevo.delete(k)
      else nuevo.add(k)
      return nuevo
    })

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="tarjeta p-3">
          <span className="block text-xs uppercase" style={{ color: "var(--fg-muted)" }}>
            Para pagar sin tocar la reserva
          </span>
          <span className="block text-lg font-semibold tabular-nums">{dinero(presupuesto)}</span>
          <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
            operativo − gastos de la semana
          </span>
        </div>
        <div className="tarjeta p-3">
          <span className="block text-xs uppercase" style={{ color: "var(--fg-muted)" }}>
            Elegido para el viernes
          </span>
          <span className="block text-lg font-semibold tabular-nums">{dinero(elegido)}</span>
          <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
            {elegidas.size} cuenta(s)
          </span>
        </div>
        <div
          className="tarjeta p-3"
          style={{ borderColor: queda < 0 ? "var(--destructive)" : "var(--success)" }}
        >
          <span className="block text-xs uppercase" style={{ color: "var(--fg-muted)" }}>
            {queda < 0 ? "Toca la reserva" : "Queda"}
          </span>
          <span className="block text-lg font-semibold tabular-nums">
            {dinero(Math.abs(queda))}
          </span>
        </div>
      </div>

      <div className="no-print flex flex-wrap items-center gap-2">
        <Boton
          tamano="sm"
          variante="fantasma"
          onClick={() => setElegidas(new Set(r.propuesta_pago))}
        >
          Volver a la propuesta
        </Boton>
        {candidatas.length > 0 && (
          <Exportar titulo="Planificador del viernes" subtitulo={subtitulo} documento={documento} />
        )}
        {puedeOrden && sinOrden.length > 0 && (
          <Boton
            tamano="sm"
            variante="primario"
            disabled={generar.isPending}
            onClick={() => generar.mutate()}
          >
            <FilePlus2 size={14} aria-hidden={true} /> Redactar las órdenes de lo elegido (
            {sinOrden.length})
          </Boton>
        )}
      </div>
      {resultado.length > 0 && (
        <ul className="text-sm" role="status">
          {resultado.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      )}
      {porAprobar.length > 0 && (
        <p className="text-sm" style={{ color: "var(--warning)" }}>
          Por aprobar antes del viernes: {porAprobar.map((p) => p.orden ?? p.titulo).join(", ")}
        </p>
      )}

      {candidatas.length === 0 && <p className="tarjeta p-4 text-sm">No hay CxP efectivas.</p>}
      {GRUPOS.map(({ titulo, de }) => {
        const filas = candidatas.filter(de)
        if (filas.length === 0) return null
        const suElegido = filas.filter((p) => elegidas.has(clave(p)))
        return (
          <section key={titulo} aria-label={titulo} className="space-y-1">
            <h3 className="flex flex-wrap items-center justify-between gap-2 text-sm font-semibold">
              {titulo}
              <span className="tabular-nums">
                {suElegido.length} de {filas.length} · elegido{" "}
                {dinero(suElegido.reduce((t, p) => t + centimos(p.usd), 0))}
              </span>
            </h3>
            <Tabla etiqueta={titulo}>
              <Cabecera>
                <Th>
                  <span className="sr-only">Elegir</span>
                </Th>
                <Th>Quién y qué</Th>
                <Th>Paso</Th>
                <Th>Vence</Th>
                <Th derecha>USD</Th>
              </Cabecera>
              <Cuerpo>
                {filas.map((p) => (
                  <Fila key={clave(p)} atenuada={!elegidas.has(clave(p))}>
                    <Td>
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        aria-label={`Pagar ${p.titulo}`}
                        checked={elegidas.has(clave(p))}
                        onChange={() => alternar(clave(p))}
                      />
                    </Td>
                    <Td>
                      {p.titulo}
                      <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                        {CLASE[p.clase] ? `${CLASE[p.clase]} · ` : ""}
                        {p.expediente && !p.detalle.includes(p.expediente)
                          ? `EXP ${p.expediente} · `
                          : ""}
                        {p.detalle}
                      </span>
                    </Td>
                    <Td>
                      <Insignia tono={TONO[p.estado ?? ""] ?? "neutro"}>
                        {r.estados_pago[p.estado ?? ""] ?? p.estado}
                      </Insignia>
                      {p.orden && (
                        <span
                          className="block text-xs tabular-nums"
                          style={{ color: "var(--fg-muted)" }}
                        >
                          {p.orden}
                        </span>
                      )}
                    </Td>
                    <Td numero>{p.fecha ? comoFecha(p.fecha) : "—"}</Td>
                    <Td derecha numero>
                      {p.usd === null ? "sin tasa" : `$ ${monto(p.usd)}`}
                    </Td>
                  </Fila>
                ))}
              </Cuerpo>
            </Tabla>
          </section>
        )
      })}
    </div>
  )
}
